/**
 * Zeitleiste nach Wochen (V-16): Termine, Deadlines und Meilensteine der
 * naechsten Wochen untereinander, jeder Eintrag verlinkt. Ein Baustein fuer
 * Training (F-34 D), Projekte und Schule (Leitlinie 3).
 */
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

import { addDays, formatDe, isoWeek, startOfWeek } from '@/lib/datetime';
import type { LocalDate } from '@/domain/types';
import { cn } from '@/lib/utils/cn';

export interface ZeitPunkt {
  key: string;
  datum: LocalDate;
  titel: string;
  /** Art fuer Screenreader, z. B. "Meilenstein". */
  art: string;
  icon: LucideIcon;
  to: string;
  /** Erledigt bzw. vorbei - abgeschwaecht. */
  erledigt?: boolean;
  /** Farbe des Symbols (Token); ohne der Akzent. */
  farbe?: string;
}

export function ZeitleisteWochen({ punkte, start, wochen, leereWochen = true }: {
  punkte: ZeitPunkt[];
  start: LocalDate;
  wochen: number;
  /** Wochen ohne Eintraege mit „–“ zeigen (sonst ausgelassen). */
  leereWochen?: boolean;
}) {
  const montage = Array.from({ length: wochen }, (_, i) => addDays(startOfWeek(start), 7 * i));
  const sortiert = [...punkte].sort((a, b) => a.datum.localeCompare(b.datum) || a.titel.localeCompare(b.titel, 'de'));
  return (
    <ol className="flex flex-col">
      {montage.map((mo) => {
        const drin = sortiert.filter((p) => p.datum >= mo && p.datum <= addDays(mo, 6));
        if (!drin.length && !leereWochen) return null;
        return (
          <li key={mo} className="flex gap-3 border-l-2 border-line py-1.5 pl-3">
            <span className="w-16 shrink-0 text-[11px] text-ink-faint">KW {isoWeek(mo)}<br />{formatDe(mo, 'dd.MM.')}</span>
            {drin.length === 0 ? <span className="text-[12px] text-ink-faint">–</span> : (
              <ul className="flex min-w-0 flex-1 flex-col gap-1">
                {drin.map((p) => (
                  <li key={p.key}>
                    <Link to={p.to} className={cn('flex items-center gap-1.5 text-sm hover:underline', p.erledigt && 'text-ink-faint line-through')}>
                      <p.icon className="size-3.5 shrink-0" style={{ color: p.farbe ?? 'var(--color-accent)' }} aria-hidden />
                      <span className="shrink-0 tabular-nums text-ink-muted">{formatDe(p.datum, 'EEEEEE dd.MM.')}</span>
                      <span className="min-w-0 truncate">{p.titel}</span>
                      <span className="sr-only">({p.art}{p.erledigt ? ', erledigt' : ''})</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}
